import { readItemField } from './item-field-reader';
import { fromBaseClone, fromBaseUuid, FromBaseStageMissing, FromBaseInputMissing, fromBaseRecord } from './from-base-context';
import type { FromBaseRow as Row } from './from-base-context';
import { layerEnchantmentOntoInventory } from './enchantment-inventory';
import type { EnchantmentInventoryReaders } from './enchantment-inventory';
import { createEnchantmentTemplateCompiler } from './enchantment-template-context';
import { mergeEnchantmentArrays } from './enchantment-template';
import type { FromBaseInventoryMetadata } from './from-base-inventory';

/** Standalone template cards use the same native builder as equipped gear. */
export function compileFromBaseEnchantmentCard(id: string, base: Row, metadata: FromBaseInventoryMetadata,
    definition: (id: string) => Row | null): Row {
    const catalog = fromBaseRecord(base.catalog) ? base.catalog : {};
    const rawDefinition = (key: string): Row | null => {
        const row = catalog[key] ?? catalog[key.toUpperCase()];
        return fromBaseRecord(row) ? row : null;
    };
    const build = createEnchantmentTemplateCompiler({ catalogRows: metadata.layerCatalog, abilityTypes: metadata.abilityTypes,
        dynamicLayers: metadata.dynamicLayers, spellCosts: metadata.spellCosts,
        aggregationStrategy: name => metadata.layerCatalog[name]?.aggregation_strategy ?? 'host_wins',
        templateRows: key => (base.enchantment_templates as Row[]).filter(row => String(readItemField(row, 'enchantment_template_id')).toUpperCase() === key.toUpperCase()),
        memberRows: key => fromBaseEnchantmentMembers(base.enchantment_template_enchantments as Row[], key, rawDefinition),
        fullItem: definition, resolveCarrierRef: definition, resolveInventoryInstance: () => null });
    const template = build(id, false);
    if (!template) throw new FromBaseInputMissing('enchantment-template', id);
    const row = mergeEnchantmentArrays(template, readItemField(template, 'layers_to_add') as Row) as Row;
    delete row.layers_to_add;
    return row;
}

/** Flat intrinsic templates write their cost before the final bucket mirror. */
export function applyFromBaseIntrinsicEnchantment(source: Row, template: unknown, readers: EnchantmentInventoryReaders,
    layerNames: readonly string[]): Row {
    const flat = fromBaseClone(source), authored = { ...flat.item_layer_types as Row };
    for (const key of layerNames) if (Object.prototype.hasOwnProperty.call(authored, key)) {
        if (!Object.prototype.hasOwnProperty.call(flat, key)) {
            const value = readItemField({ [key]: [authored[key]] }, key);
            flat[key] = Array.isArray(value) ? value : [value];
        }
        delete authored[key];
    }
    flat.item_layer_types = authored;
    const result = layerEnchantmentOntoInventory(flat, template, false, readers) as Row;
    const layers = { ...result.item_layer_types as Row };
    // Native intrinsic grants precede the bucket mirror. Preserve their complete
    // root lists so an authored bucket cannot shadow newly appended passives.
    for (const key of layerNames) if (Object.prototype.hasOwnProperty.call(result, key))
        layers[key] = readItemField({ [key]: [result[key]] }, key);
    delete layers.cost_gp;
    const cost = readers.layerValue({ ...result, item_layer_types: layers }, 'cost_gp');
    result.item_layer_types = { ...layers, cost_gp: [cost] };
    delete result.cost_gp;
    return result;
}

/** Native template-member SELECT order, shared by definitions and saved/static gear. */
export function fromBaseEnchantmentMembers(rows: readonly Row[], template: string, definition: (id: string) => Row | null): Row[] {
    const selected = rows.filter(row => String(readItemField(row, 'enchantment_template_id')).toUpperCase() === template.toUpperCase());
    for (const row of selected) if (!Number.isSafeInteger(readItemField(row, 'enchantment_template_enchantment_i')))
        throw new FromBaseStageMissing('enchantment-source-order', template);
    return selected.sort((a, b) => Number(readItemField(a, 'enchantment_template_enchantment_i')) - Number(readItemField(b, 'enchantment_template_enchantment_i')))
        .map(row => {
            const id = readItemField(row, 'enchantment_item_id');
            return { ...row, item_name: fromBaseUuid(id) ? readItemField(definition(id), 'item_name') : null };
        });
}
